/**
 * AP-39 — Posteingang (Modul INBOX). Spezifikation: docs/Arbeitspakete/AP-39/AP-39_Posteingang.md.
 */

/** 5. — auf welchem Weg ein Dokument hereinkam */
export const INBOX_SOURCES = ['UPLOAD', 'FOLDER', 'MAIL'] as const
export type InboxSource = (typeof INBOX_SOURCES)[number]

/** 6. — Lebenslauf eines Eintrags */
export const INBOX_STATUSES = ['NEW', 'UNASSIGNED', 'SUGGESTED', 'ASSIGNED', 'DONE', 'IGNORED', 'DUPLICATE', 'FAILED'] as const
export type InboxStatus = (typeof INBOX_STATUSES)[number]

/** 6. — die Status, die im Menükopf zählen: hier muss jemand etwas tun */
export const INBOX_OPEN_STATUSES: readonly InboxStatus[] = ['UNASSIGNED', 'SUGGESTED', 'FAILED']

/** 7.4 — Art eines Dokuments */
export const INBOX_KINDS = [
  'OFFER',
  'ORDER_CONFIRMATION',
  'INVOICE',
  'SUPPLIER_DELIVERY_NOTE',
  'SIGNED_DELIVERY_NOTE',
  'CERTIFICATE',
  'OTHER',
  'UNKNOWN',
] as const
export type InboxKind = (typeof INBOX_KINDS)[number]

/** 6. — Kennzeichen neben dem Status */
// `PARTNER_AI` (10.10.2026): Der Absender steht nur fest, weil die KI seine UID gelesen hat — solange das so ist,
// wird der Eintrag nicht von selbst zugeordnet (AP-39 8.2, fünfte Bedingung).
export const INBOX_FLAGS = ['UNKNOWN_SENDER', 'AUTO_MAIL', 'HELD', 'DEVIATION', 'OFFER_EXPIRES', 'PARTNER_AI'] as const
export type InboxFlag = (typeof INBOX_FLAGS)[number]

/** 5.4 — Endungen, die der Posteingang annimmt; alles andere wird „nicht angenommen" */
export const INBOX_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'webp', 'docx', 'xlsx', 'xml', 'eml', 'msg'] as const

/** 12.1 — der Zähler im Menükopf */
export interface InboxCounter {
  open: number
  failed: number
  /** Ordner oder Eingangsadresse arbeitet nicht (5.2-5) */
  warning: boolean
  /** Phase E: warum und seit wann der Ordner nicht arbeitet — für „Ordner nicht verbunden — seit …" */
  folderError?: string | null
  folderErrorSince?: string | null
}

// ── AP-39 Nachtrag 1: die Zeilen eines Ziels mit dem, was aus dem Dokument gelesen wurde ──────────

/** Was an einem Ziel gelesen wird: das Angebot zur Preisanfrage, die Auftragsbestätigung zur Bestellung. */
export const INBOX_VALUE_KINDS = ['OFFER', 'ORDER_CONFIRMATION'] as const
export type InboxValueKind = (typeof INBOX_VALUE_KINDS)[number]

/** Die Werte einer Zeile — was am Ziel gilt oder was gelesen wurde. Preise nur mit dem Preisrecht des Ziels. */
export interface InboxLineValues {
  unitPrice?: number | null
  priceUnit?: number | null
  discountPercent?: number | null
  /** EP ÷ Preiseinheit × (1 − Rabatt) */
  netPrice?: number | null
  /** Einheit, in der der Preis gilt, wenn sie von der Zeile abweicht (Kilopreis an einer Meter-Zeile) */
  unitCode?: string | null
  /** Auftragsbestätigung: bestätigte Menge; zusätzliche Zeile: ihre Menge */
  quantity?: number | null
  /** Auftragsbestätigung: bestätigter Liefertermin (JJJJ-MM-TT) */
  deliveryAt?: string | null
  /** Angebot: Lieferzeit in Tagen */
  leadTimeDays?: number | null
  notOffered?: boolean
  alternative?: string | null
  /** Artikelnummer des Lieferanten */
  articleNumber?: string | null
}

/** Was gelesen wurde und noch nicht übernommen ist. */
export interface InboxReadValues extends InboxLineValues {
  confidence: 'hoch' | 'mittel' | 'niedrig' | null
  /** das Zitat steht so nicht auf der Seite (`false`); ohne Textebene nicht prüfbar (`null`) */
  quoteFound: boolean | null
  /** gelesene Einheit, die sich nicht übernehmen ließ — der Preis gilt so nicht je Einheit der Zeile */
  unitHint: string | null
}

/** Wo im Dokument die Zeile steht — für die Markierung im Original. */
export interface InboxLineSource {
  page: number | null
  /** wörtlicher Ausschnitt der Zeile mit dem Preis bzw. Termin */
  quote: string | null
  /** Bezeichnung, wie sie im Dokument steht */
  text: string | null
}

/**
 * Kennzeichen einer Zeile: `NOT_OFFERED` der Lieferant bietet sie ausdrücklich nicht an · `ALTERNATIVE` er
 * bietet etwas anderes an · `EXTRA` steht im Dokument, aber nicht im Beleg · `MISSING` steht im Beleg, aber
 * nicht im Dokument · `DEVIATION` weicht von der Bestellung ab (Menge, Preis, Termin).
 */
export const INBOX_LINE_MARKS = ['NOT_OFFERED', 'ALTERNATIVE', 'EXTRA', 'MISSING', 'DEVIATION'] as const
export type InboxLineMark = (typeof INBOX_LINE_MARKS)[number]

export interface InboxValuesLine {
  /** Kennung der Zeile in dieser Ansicht */
  key: string
  /** Kennung zum Übernehmen und Verwerfen; `null`, wenn an der Zeile nichts Gelesenes offen ist */
  readId: string | null
  /** Zeile des Ziels; `null` = zusätzliche Zeile des Dokuments */
  lineId: string | null
  position: string | null
  name: string
  articleNumber: string | null
  quantity: number | null
  unitCode: string | null
  /** Bestellung: was bestellt war — Preis nur mit Preisrecht —, und der gewünschte Termin */
  ordered?: { unitPrice?: number | null; priceUnit?: number | null; discountPercent?: number | null; deliveryDate?: string | null }
  /** was am Ziel gilt — übernommen oder von Hand erfasst */
  values: InboxLineValues | null
  /** was gelesen wurde und noch nicht übernommen ist */
  read: InboxReadValues | null
  source: InboxLineSource | null
  confirmedAt: string | null
  marks: InboxLineMark[]
  /** Abweichungen von der Bestellung in Worten — ohne Betrag */
  deviations: string[]
}

/** Ein Feld des Kopfs: Nummer und Datum des Dokuments beim Absender, beim Angebot die Bindefrist. */
export interface InboxValuesHeadField {
  key: 'number' | 'date' | 'validUntil'
  value: string | null
  read: string | null
}

/** Warum sich Gelesenes hier nicht übernehmen lässt. */
export type InboxValuesBlock = 'PRICE_RIGHT' | 'EDIT_RIGHT' | 'CLOSED'

/** Was das Ziel über seine Zeilen sagt — der Posteingang setzt Ziel, Bezeichnung und Verweis dazu. */
export interface InboxTargetValues {
  kind: InboxValueKind
  /** Preise sind zu sehen (Feldrecht des Ziels); ohne es bleibt auch das Gelesene verborgen — es sind Preise */
  prices: boolean
  /** Gelesenes lässt sich übernehmen und verwerfen: Bearbeitungs- und Preisrecht des Ziels, Ziel offen */
  canConfirm: boolean
  blocked: InboxValuesBlock | null
  head: InboxValuesHeadField[]
  /** der gelesene Kopf ist noch nicht übernommen */
  headOpen: boolean
  lines: InboxValuesLine[]
  /** Zahl der Zeilen, an denen Gelesenes offen ist — auch ohne Preisrecht */
  open: number
  /** woraus zuletzt gelesen wurde */
  readFrom: { fileName: string | null; model: string | null } | null
  /** nur mit Preisrecht: Summe netto dessen, was am Ziel gilt */
  total?: number | null
  /** Bestellung: Stand der Bestätigung in Worten („bestätigt mit Abweichung") */
  stateText?: string | null
}

/** `GET /inbox/items/:id/values` — je Zuordnung an einem Ziel mit Werten eine Ansicht. */
export interface InboxValuesView extends InboxTargetValues {
  targetKind: string
  targetId: string
  label: string
  url: string | null
}
